import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import useSimulationStore from '../../../store/simulationStore';

interface VelocityHistogramChartProps {
    height?: number;
}

// Color palette
const COLORS = [
    '#4fc3f7', '#81c784', '#ffb74d', '#f48fb1', '#ce93d8',
    '#90caf9', '#a5d6a7', '#ffe082', '#ef9a9a', '#b39ddb'
];

const MAX_DATA_POINTS = 60;
const UPDATE_INTERVAL = 500;

interface DataPoint {
    time: number;
    [species: string]: number;
}

const VelocityHistogramChart: React.FC<VelocityHistogramChartProps> = memo(({ height = 250 }) => {
    const [history, setHistory] = useState<DataPoint[]>([]);
    const timeRef = useRef(0);
    const lastUpdateRef = useRef(0);

    const { averageVelocitiesBySpecies, isRunning } = useSimulationStore(
        state => ({
            averageVelocitiesBySpecies: state.averageVelocitiesBySpecies || {},
            isRunning: state.isRunning,
        }),
        shallow
    );

    const speciesNames = useMemo(() =>
        Object.keys(averageVelocitiesBySpecies),
        [averageVelocitiesBySpecies]
    );

    // Throttled update for time-series
    useEffect(() => {
        if (!isRunning) return;

        const interval = setInterval(() => {
            const now = Date.now();
            if (now - lastUpdateRef.current < UPDATE_INTERVAL) return;
            lastUpdateRef.current = now;

            timeRef.current += UPDATE_INTERVAL / 1000;

            const velocities: DataPoint = { time: timeRef.current };
            Object.entries(averageVelocitiesBySpecies).forEach(([name, vel]) => {
                if (typeof vel === 'number') {
                    velocities[name] = vel;
                } else if (vel && typeof vel === 'object' && 'x' in vel) {
                    const vec = vel as { x: number; y: number; z: number };
                    velocities[name] = Math.sqrt(vec.x * vec.x + vec.y * vec.y + vec.z * vec.z);
                } else {
                    velocities[name] = 0;
                }
            });

            setHistory(prev => {
                const updated = [...prev, velocities];
                return updated.length > MAX_DATA_POINTS
                    ? updated.slice(-MAX_DATA_POINTS)
                    : updated;
            });
        }, UPDATE_INTERVAL);

        return () => clearInterval(interval);
    }, [isRunning, averageVelocitiesBySpecies]);

    // Reset on simulation reset
    useEffect(() => {
        if (!isRunning && history.length > 0 && speciesNames.length === 0) {
            setHistory([]);
            timeRef.current = 0;
        }
    }, [isRunning, speciesNames.length, history.length]);

    const avgVelocity = useMemo(() => {
        const values = Object.values(averageVelocitiesBySpecies).map(v => {
            if (typeof v === 'number') return v;
            if (v && typeof v === 'object' && 'x' in v) {
                // It's a vector, calculate magnitude
                const vec = v as { x: number; y: number; z: number };
                return Math.sqrt(vec.x * vec.x + vec.y * vec.y + vec.z * vec.z);
            }
            return 0;
        });
        if (values.length === 0) return 0;
        return values.reduce((sum, v) => sum + v, 0) / values.length;
    }, [averageVelocitiesBySpecies]);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Summary */}
            <div style={summaryStyle}>
                <span>Avg Velocity: <strong style={{ color: '#4fc3f7' }}>{avgVelocity.toFixed(2)}</strong></span>
                <span>Species: <strong style={{ color: '#81c784' }}>{speciesNames.length}</strong></span>
                <span>Points: <strong style={{ color: '#ffb74d' }}>{history.length}</strong></span>
            </div>

            {/* Chart */}
            <div style={{ flex: 1, minHeight: 0 }}>
                {history.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={history} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis
                                dataKey="time"
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                                tickFormatter={(v) => `${v.toFixed(0)}s`}
                            />
                            <YAxis
                                stroke="#888"
                                tick={{ fill: '#ccc', fontSize: 10 }}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                    border: '1px solid #4fc3f7',
                                    borderRadius: '4px',
                                    color: '#fff'
                                }}
                                formatter={(value: number) => value.toFixed(3)}
                                labelFormatter={(label) => `Time: ${Number(label).toFixed(1)}s`}
                            />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            {speciesNames.map((species, index) => (
                                <Line
                                    key={species}
                                    type="monotone"
                                    dataKey={species}
                                    stroke={COLORS[index % COLORS.length]}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={emptyStateStyle}>
                        <span>Collecting velocity data...</span>
                        <span style={{ fontSize: '10px', color: '#666' }}>Start simulation to see velocities over time</span>
                    </div>
                )}
            </div>
        </div>
    );
});

VelocityHistogramChart.displayName = 'VelocityHistogramChart';

const summaryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(40, 40, 40, 0.5)',
    borderRadius: '4px',
    marginBottom: '8px',
    fontSize: '11px',
    color: '#ccc',
    fontFamily: 'monospace',
};

const emptyStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#888',
    fontSize: '12px',
    gap: '8px',
};

export default VelocityHistogramChart;
